import { slideDown, slideUp } from '../../utils/slide-functions';
import { generateUniqueIdWithIdPrefix } from '../../utils/unique-id';

export const setupToggles = () => {
  const toggleTransitionSpeed = 200;
  const toggles: NodeListOf<HTMLElement> = document.querySelectorAll('[data-toggle]:not([data-init])');

  toggles.forEach((toggle) => {
    const relatedData = toggle.getAttribute('data-target');
    const toggleContent = document.querySelector(`[id="${relatedData}"]`) as HTMLElement;
    const parentID = toggle.getAttribute('data-parent');
    const isOverlay = toggle.getAttribute('data-toggle') === 'overlay' || false;
    const isTabs = (toggle.getAttribute('data-toggle') === 'tab' && parentID) || false;
    const isHold = toggle.getAttribute('data-hold') || false;
    const display = toggle.getAttribute('data-display') || 'block';
    const htmlTag = document.documentElement;

    let toggleID = toggle.getAttribute('id');
    let timeoutItem: ReturnType<typeof setTimeout> | null;

    /* eslint-disable-next-line no-inner-declarations */
    function dataToggleHandler(e: KeyboardEvent | MouseEvent | TouchEvent) {
      let requiresTrigger = false;

      // Check if escape is keyup-ed
      if ((e as KeyboardEvent).key === 'Escape') {
        requiresTrigger = true;
        toggle.focus();
      } else {
        //otherwise check if touch/click is outside of bounds
        const target = e.target as HTMLElement;

        if (!target.closest(`[id="${relatedData}"]`) && !target.closest(`[id="${toggleID}"]`)) {
          requiresTrigger = true;
        }
      }

      if (requiresTrigger) {
        toggleControl();
        // Clear timeout to help prevent focus / other data toggle press conflicts
        if (timeoutItem) {
          clearTimeout(timeoutItem);
        }
        timeoutItem = null;
      }
    }
    /* eslint-disable-next-line no-inner-declarations */
    function toggleControl() {
      if (isOverlay) {
        htmlTag.classList.remove('js-data-toggled');
      }

      if (toggle.classList.contains('active')) {
        let shouldSlideUp = false;
        if (!isTabs) {
          shouldSlideUp = true;
        } else {
          // Check to see if any other of your tab siblings are also open... if so close
          const parentWrap = document.getElementById(parentID!);
          const relatedTabs = parentWrap!.querySelectorAll('[data-toggle="tab"]');
          let counter = 0;
          for (const element of relatedTabs) {
            const tab = element;
            const expanded = tab.getAttribute('aria-expanded');
            if (expanded === 'true') {
              counter++;
            }
          }
          if (counter > 1) {
            shouldSlideUp = true;
          }
        }

        if (shouldSlideUp) {
          htmlTag.removeEventListener('click', dataToggleHandler);
          htmlTag.removeEventListener('touchstart', dataToggleHandler);
          htmlTag.removeEventListener('keyup', dataToggleHandler);

          if (toggleContent) {
            slideUp(toggleContent, toggleTransitionSpeed);
          }

          toggle.classList.remove('active');
          toggle.setAttribute('aria-expanded', 'false');
        }
      } else {
        toggle.classList.add('active');
        toggle.setAttribute('aria-expanded', 'true');

        if (toggleContent) {
          slideDown(toggleContent, toggleTransitionSpeed, display);
        }

        setTimeout(function () {
          if (isOverlay) {
            htmlTag.classList.add('js-data-toggled');
          }

          const later = function () {
            const inputs = toggleContent.querySelectorAll('input, select, textarea');

            if (inputs.length > 0) {
              (inputs[0] as HTMLElement).focus();
            } else {
              toggleContent.focus();
            }
          };

          timeoutItem = setTimeout(later, toggleTransitionSpeed);
        }, toggleTransitionSpeed);

        if (!isHold) {
          htmlTag.addEventListener('click', dataToggleHandler);
          htmlTag.addEventListener('touchstart', dataToggleHandler);
          htmlTag.addEventListener('keyup', dataToggleHandler);
        }
      }
    }

    if (relatedData) {
      const isExpanded = toggle.classList.contains('active');

      // Make sure there is an ID set for the toggle for a11y needs
      if (!toggleID) {
        toggle.setAttribute('id', generateUniqueIdWithIdPrefix());
        toggleID = toggle.getAttribute('id');
        // Finish up a11y setup for related element
        toggleContent?.setAttribute('aria-labelledby', toggleID!);
      }

      // Indicate initialization
      toggle.setAttribute('data-init', '');

      // Setup proper roles for a11y and then bind interaction functionality
      if (toggle.tagName !== 'BUTTON') {
        toggle.setAttribute('role', 'button');
      }

      toggle.setAttribute('aria-expanded', isExpanded.toString());

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        toggleControl();
      });

      if (isExpanded && !isHold) {
        htmlTag.addEventListener('click', dataToggleHandler);
        htmlTag.addEventListener('touchstart', dataToggleHandler);
        htmlTag.addEventListener('keyup', dataToggleHandler);
      }
    }
  });
};
